import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
  Chip,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAppSelector } from '../store/hooks';
import Header from './Header';
import { API_ORIGIN } from '../config/api';
import {
  fetchMyDigipinAccess,
  claimDigipinApiKey,
  type DigipinKeyMeta,
  type DigipinRequest,
} from '../services/digipinAccessService';

/**
 * ProfileManagement Component
 * Allows registered users to view and update their profile information
 * and manage their API keys in the profile section.
 */
const ProfileManagement = () => {
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  // Form state
  const [profession, setProfession] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');

  // API Key state
  const [digipinRequest, setDigipinRequest] = useState<DigipinRequest | null>(null);
  const [digipinKeys, setDigipinKeys] = useState<DigipinKeyMeta[]>([]);
  const [claiming, setClaiming] = useState(false);
  const [claimedKey, setClaimedKey] = useState<string | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    profession?: string;
    state?: string;
    country?: string;
  }>({});

  /**
   * Fetch user profile and API key status on component mount
   */
  useEffect(() => {
    const fetchProfileAndKeys = async () => {
      if (!user || !user.id) {
        setError('User information not available. Please log in again.');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_ORIGIN}/api/profile/${user.id}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`,
          },
        });

        const contentType = response.headers?.get?.('content-type') || 'application/json';
        if (contentType && !contentType.includes('application/json')) {
          throw new Error(
            'Profile API returned a non-JSON response. The server may be misconfigured.'
          );
        }

        const data = await response.json();

        if (response.status === 404) {
          setProfession('');
          setState('');
          setCountry('');
        } else if (!response.ok) {
          setError(data.message || 'Failed to load profile. Please try again.');
        } else if (data.success && data.profile) {
          setProfession(data.profile.profession || '');
          setState(data.profile.state || '');
          setCountry(data.profile.country || '');
        } else {
          setError('Failed to load profile data.');
        }

        // Fetch API key access after profile request resolves
        try {
          const keyData = await fetchMyDigipinAccess();
          if (keyData && keyData.success) {
            setDigipinRequest(keyData.request || null);
            setDigipinKeys(keyData.keys || []);
          }
        } catch {
          // Silently handle API key fetch failure
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Network error. Please try again.';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileAndKeys();
  }, [user]);

  const activeKey = digipinKeys.find((k) => k.status === 'active');
  const canClaim = digipinKeys.some((k) => k.has_one_time_key);
  const digipinStatus = digipinRequest?.status;

  const onClaimKey = async () => {
    setClaiming(true);
    try {
      const res = await claimDigipinApiKey();
      if (res && res.key) {
        setClaimedKey(res.key);
        const data = await fetchMyDigipinAccess().catch(() => null);
        if (data && data.success) {
          setDigipinKeys(data.keys || []);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not claim key');
    } finally {
      setClaiming(false);
    }
  };

  /**
   * Validate form fields
   */
  const validateForm = (): boolean => {
    const errors: { profession?: string; state?: string; country?: string } = {};

    if (!profession.trim()) {
      errors.profession = 'Profession is required';
    }

    if (!state.trim()) {
      errors.state = 'State is required';
    }

    if (!country.trim()) {
      errors.country = 'Country is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError(null);
    setSuccessMessage(null);
    setValidationErrors({});

    if (!validateForm()) {
      return;
    }

    if (!user || !user.id) {
      setError('User information not available. Please log in again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const csrfMatch = document.cookie.match(/(^|;)\s*_csrf_token\s*=\s*([^;]+)/);
      if (csrfMatch && csrfMatch[2]) {
        headers['X-CSRF-Token'] = csrfMatch[2];
      }

      const response = await fetch(`${API_ORIGIN}/api/profile/${user.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          ...headers,
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`,
        },
        body: JSON.stringify({
          profession: profession.trim(),
          state: state.trim(),
          country: country.trim(),
        }),
      });

      const contentType = response.headers?.get?.('content-type') || 'application/json';
      if (contentType && !contentType.includes('application/json')) {
        throw new Error(
          'Profile API returned a non-JSON response. The server may be misconfigured.'
        );
      }

      const data = await response.json();

      if (!response.ok) {
        if (data.errors && Array.isArray(data.errors)) {
          setError(data.errors.join(', '));
        } else {
          setError(data.message || 'Failed to update profile. Please try again.');
        }
        return;
      }

      if (data.success) {
        setSuccessMessage('Profile updated successfully!');
      } else {
        setError('Failed to update profile. Please try again.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Network error. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearError = () => {
    setError(null);
  };

  const handleCloseSuccess = () => {
    setSuccessMessage(null);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <>
      <Header subtitle="Profile" active="profile" />
      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={3}>
            {/* Main Profile Form Card */}
            <Paper
              elevation={6}
              sx={{
                padding: { xs: 3, sm: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: 2,
              }}
            >
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  marginBottom: 1,
                  textAlign: 'center',
                }}
              >
                Profile Management
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  marginBottom: 3,
                  textAlign: 'center',
                }}
              >
                View and update your profile information
              </Typography>

              {error && (
                <Alert
                  severity="error"
                  onClose={handleClearError}
                  sx={{ width: '100%', marginBottom: 3 }}
                >
                  {error}
                </Alert>
              )}

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ width: '100%', maxWidth: '500px' }}
                noValidate
              >
                <TextField
                  fullWidth
                  label="Profession"
                  variant="outlined"
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                  error={!!validationErrors.profession}
                  helperText={validationErrors.profession}
                  disabled={isSubmitting}
                  required
                  sx={{ marginBottom: 2 }}
                  placeholder="e.g., GIS Analyst, Urban Planner, Geographer"
                />

                <TextField
                  fullWidth
                  label="State"
                  variant="outlined"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  error={!!validationErrors.state}
                  helperText={validationErrors.state}
                  disabled={isSubmitting}
                  required
                  sx={{ marginBottom: 2 }}
                  placeholder="e.g., California, New York"
                />

                <TextField
                  fullWidth
                  label="Country"
                  variant="outlined"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  error={!!validationErrors.country}
                  helperText={validationErrors.country}
                  disabled={isSubmitting}
                  required
                  sx={{ marginBottom: 3 }}
                  placeholder="e.g., United States, Canada"
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isSubmitting}
                  sx={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    textTransform: 'none',
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ marginRight: 1 }} />
                      Saving...
                    </>
                  ) : (
                    'Update Profile'
                  )}
                </Button>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  marginTop: 3,
                  textAlign: 'center',
                  maxWidth: '500px',
                }}
              >
                All fields are required. Keep your profile information up to date.
              </Typography>
            </Paper>

            {/* API Key Section */}
            <Paper
              elevation={6}
              sx={{
                padding: { xs: 3, sm: 4 },
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <KeyIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  API Keys & Developer Access
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                Manage your DIGIPIN Cloud API keys for high-speed online batch encoding, QGIS plugin
                integration, and API services.
              </Typography>

              <Divider sx={{ mb: 2.5 }} />

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Status:
                  </Typography>
                  <Chip
                    size="small"
                    label={(digipinStatus || 'No Active Request').toUpperCase()}
                    color={
                      digipinStatus === 'approved'
                        ? 'success'
                        : digipinStatus === 'pending'
                          ? 'warning'
                          : 'default'
                    }
                    sx={{ fontWeight: 700 }}
                  />
                  {activeKey && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Key: ${activeKey.key_prefix}…`}
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>

                {canClaim && (
                  <Alert
                    severity="info"
                    action={
                      <Button
                        variant="contained"
                        size="small"
                        disabled={claiming}
                        onClick={onClaimKey}
                        sx={{ fontWeight: 700 }}
                      >
                        {claiming ? 'Claiming...' : 'Claim Key'}
                      </Button>
                    }
                  >
                    Your DIGIPIN API Key is approved and ready to claim!
                  </Alert>
                )}

                <Box sx={{ pt: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<KeyIcon />}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate('/developers/digipin')}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    {digipinStatus ? 'Manage API Keys' : 'Get API Key'}
                  </Button>
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Container>

        {/* Key Claim Dialog */}
        <Dialog open={!!claimedKey} onClose={() => setClaimedKey(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Your DIGIPIN API Key</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Copy this key into QGIS or your application now. It will not be displayed again.
            </Alert>
            <Box
              sx={{
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                color: 'primary.main',
                wordBreak: 'break-all',
                fontSize: 14,
              }}
            >
              {claimedKey}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={() => claimedKey && navigator.clipboard.writeText(claimedKey)}
            >
              Copy Key
            </Button>
            <Button variant="contained" onClick={() => setClaimedKey(null)}>
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSuccess}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default ProfileManagement;
